"use client";

import { useEffect } from "react";

// When a Next.js JS/CSS chunk fails to load (e.g. the server briefly dropped the
// connection, or chunk hashes changed after a redeploy), reload the page once so
// the user gets the working version instead of "This page couldn't load".
// A short cooldown in sessionStorage prevents reload loops if it keeps failing.
const KEY = "chunk-reload-at";
const COOLDOWN_MS = 10_000;

function isChunkError(msg?: string | null): boolean {
  if (!msg) return false;
  return (
    /ChunkLoadError/i.test(msg) ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

// A failed RSC navigation/prefetch fetch (the server briefly dropped the
// connection) surfaces as an UNHANDLED rejection like these. Handled fetches
// (with try/catch, e.g. the booking form) never reach here, so this won't
// reload on normal, recovered errors.
function isNetworkError(msg?: string | null): boolean {
  if (!msg) return false;
  return (
    isChunkError(msg) ||
    /network error/i.test(msg) ||
    /failed to fetch/i.test(msg) ||
    /load failed/i.test(msg) ||
    /networkerror/i.test(msg)
  );
}

export function ChunkReloadGuard() {
  useEffect(() => {
    function reloadOnce() {
      // Don't reload a hidden/background tab.
      if (document.visibilityState === "hidden") return;
      try {
        const last = Number(sessionStorage.getItem(KEY) || 0);
        if (Date.now() - last < COOLDOWN_MS) return; // avoid loops
        sessionStorage.setItem(KEY, String(Date.now()));
      } catch {
        // sessionStorage unavailable — still reload once.
      }
      window.location.reload();
    }

    function onError(e: Event) {
      // Failed <script>/<link> resource is caught in the capture phase.
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "SCRIPT" || el.tagName === "LINK")) {
        const url =
          (el as HTMLScriptElement).src || (el as HTMLLinkElement).href || "";
        if (url.includes("/_next/static/")) reloadOnce();
        return;
      }
      if (isChunkError((e as ErrorEvent).message)) reloadOnce();
    }

    function onRejection(e: PromiseRejectionEvent) {
      const reason = e.reason;
      const msg = typeof reason === "string" ? reason : reason?.message;
      // Failed chunk OR a failed RSC navigation/prefetch fetch — recover by
      // reloading once (cooldown-guarded so it never loops).
      if (isNetworkError(msg)) reloadOnce();
    }

    // Capture phase so resource-load errors (which don't bubble) are seen.
    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
